import { createAsyncThunk, createEntityAdapter, createSlice, createSelector } from '@reduxjs/toolkit';
import { startLoading, stopLoading } from 'app/store/utilSlice';
import { apiGet, apiPut, apiPost, apiDel } from 'app/shared-components/util/restAPI';

const superbillAdapter = createEntityAdapter({
  sortComparer: (a, b) => b.generatedDate - a.generatedDate
});

export const fetchSuperbillsByOwner = createAsyncThunk(
  'superbills/fetchByOwner',
  async ({ ownerKey, from, to }, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = `/x3/superbill/byowner/${ownerKey}`;
      const options = { 
        headers: {}, 
        response: false,
        queryStringParameters: from && to ? { rangeCondition: 'BETWEEN', rangeValue1: from, rangeValue2: to } : {}
      };
      return await apiGet(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const fetchSuperbillsByClient = createAsyncThunk(
  'superbills/fetchByClient',
  async ({ clientId, from, to }, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = `/x3/superbill/byclient/${clientId}`;
      const options = { 
        headers: {}, 
        response: false,
        queryStringParameters: from && to ? { rangeCondition: 'BETWEEN', rangeValue1: from, rangeValue2: to } : {}
      };
      return await apiGet(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const createSuperbill = createAsyncThunk(
  'superbills/create',
  async ({ ownerKey, superbillData }, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = '/x3/superbill/createwithrecords';
      const options = { headers: {}, body: superbillData, response: false };
      return await apiPost(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const updateSuperbill = createAsyncThunk(
  'superbills/update',
  async ({ superbillId, updates }, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = `/x3/superbill/${superbillId}`;
      const body = { id: superbillId, ...updates };
      const options = { headers: {}, body, response: false };
      return await apiPut(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const finalizeSuperbill = createAsyncThunk(
  'superbills/finalize',
  async (superbillId, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = `/x3/superbill/${superbillId}/finalize`;
      const options = { headers: {}, response: false };
      return await apiPost(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const deleteSuperbill = createAsyncThunk(
  'superbills/delete',
  async (superbillId, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = `/x3/superbill/${superbillId}`;
      const options = { headers: {}, response: false };
      await apiDel(apiName, path, options);
      return superbillId;
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const generateSuperbillPDF = createAsyncThunk(
  'superbills/generatePDF',
  async (superbillId, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = `/x3/superbill/${superbillId}/generatepdf`;
      const options = { headers: {}, response: false };
      return await apiPost(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const regenerateSuperbillPDF = createAsyncThunk(
  'superbills/regeneratePDF',
  async (superbillId, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = `/x3/superbill/${superbillId}/regeneratepdf`;
      const options = { headers: {}, response: false };
      return await apiPost(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const downloadSuperbillPDF = createAsyncThunk(
  'superbills/downloadPDF',
  async (superbillId, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = `/x3/superbill/${superbillId}/downloadpdf`;
      const options = { headers: {}, response: false };
      const response = await apiGet(apiName, path, options);
      if (response.downloadUrl) {
        window.open(response.downloadUrl, '_blank');
      }
      return response;
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const sendSuperbillToClient = createAsyncThunk(
  'superbills/sendToClient',
  async ({ superbillId, taskTitle, taskDescription }, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = `/x3/superbill/${superbillId}/sendtoclient`;
      const options = { 
        headers: {}, 
        response: false,
        queryStringParameters: {
          ...(taskTitle && { taskTitle }),
          ...(taskDescription && { taskDescription })
        }
      };
      return await apiPost(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const processSuperbillPayment = createAsyncThunk(
  'superbills/processPayment',
  async (superbillId, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = `/x3/superbill/${superbillId}/processpayment`;
      const options = { headers: {}, response: false };
      return await apiPost(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const getSuperbillVersionHistory = createAsyncThunk(
  'superbills/getVersionHistory',
  async (superbillId, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = `/x3/superbill/${superbillId}/versions`;
      const options = { headers: {}, response: false };
      return await apiGet(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

const superbillsSlice = createSlice({
  name: 'superbills',
  initialState: superbillAdapter.getInitialState({
    byOwnerKey: {},
    byClient: {}
  }),
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchSuperbillsByOwner.fulfilled, (state, action) => {
        const ownerKey = action.meta.arg.ownerKey;
        state.byOwnerKey[ownerKey] = action.payload.map(s => s.id);
        superbillAdapter.upsertMany(state, action.payload);
      })
      .addCase(fetchSuperbillsByClient.fulfilled, (state, action) => {
        const clientId = action.meta.arg.clientId;
        state.byClient[clientId] = action.payload.map(s => s.id);
        superbillAdapter.upsertMany(state, action.payload);
      })
      .addCase(createSuperbill.fulfilled, (state, action) => {
        const ownerKey = action.meta.arg.ownerKey;
        const clientId = action.payload.clientId;
        superbillAdapter.addOne(state, action.payload);
        
        if (!state.byOwnerKey[ownerKey]) state.byOwnerKey[ownerKey] = [];
        if (!state.byOwnerKey[ownerKey].includes(action.payload.id)) {
          state.byOwnerKey[ownerKey].push(action.payload.id);
        }
        
        if (!state.byClient[clientId]) state.byClient[clientId] = [];
        if (!state.byClient[clientId].includes(action.payload.id)) {
          state.byClient[clientId].push(action.payload.id);
        }
      })
      .addCase(updateSuperbill.fulfilled, (state, action) => {
        superbillAdapter.upsertOne(state, action.payload);
      })
      .addCase(finalizeSuperbill.fulfilled, (state, action) => {
        superbillAdapter.upsertOne(state, action.payload);
      })
      .addCase(deleteSuperbill.fulfilled, (state, action) => {
        const superbillId = action.payload;
        superbillAdapter.removeOne(state, superbillId);
        
        Object.keys(state.byOwnerKey).forEach(key => {
          state.byOwnerKey[key] = state.byOwnerKey[key].filter(id => id !== superbillId);
        });
        Object.keys(state.byClient).forEach(key => {
          state.byClient[key] = state.byClient[key].filter(id => id !== superbillId);
        });
      })
      .addCase(generateSuperbillPDF.fulfilled, (state, action) => {
        const superbillId = action.meta.arg;
        const existingSuperbill = state.entities[superbillId];
        if (existingSuperbill && action.payload.fileId) {
          existingSuperbill.pdfFileId = action.payload.fileId;
        }
      })
      .addCase(regenerateSuperbillPDF.fulfilled, (state, action) => {
        const superbillId = action.meta.arg;
        const existingSuperbill = state.entities[superbillId];
        if (existingSuperbill && action.payload.fileId) {
          existingSuperbill.pdfFileId = action.payload.fileId;
        }
      })
      .addCase(processSuperbillPayment.fulfilled, (state, action) => {
        superbillAdapter.upsertOne(state, action.payload);
      })
      .addCase(sendSuperbillToClient.fulfilled, (state, action) => {
        const superbillId = action.meta.arg.superbillId;
        const existingSuperbill = state.entities[superbillId];
        if (existingSuperbill && action.payload.pdfFileId) {
          existingSuperbill.pdfFileId = action.payload.pdfFileId;
        }
      });
  }
});

const { selectById, selectAll } = superbillAdapter.getSelectors();

export const selectSuperbillsByOwnerKey = (ownerKey) => createSelector(
  [state => state.superbills.byOwnerKey, state => state.superbills],
  (byOwnerKey, superbillsState) => {
    const superbillIds = byOwnerKey[ownerKey] || [];
    return superbillIds.map(id => selectById(superbillsState, id)).filter(Boolean);
  }
);

export const selectSuperbillsByClient = (clientId) => createSelector(
  [state => state.superbills.byClient, state => state.superbills],
  (byClient, superbillsState) => {
    const superbillIds = byClient[clientId] || [];
    return superbillIds.map(id => selectById(superbillsState, id)).filter(Boolean);
  }
);

export const selectSuperbillById = (id) => state => selectById(state.superbills, id);

export const selectSuperbillsByStatus = (clientId, status) => createSelector(
  [selectSuperbillsByClient(clientId)],
  (superbills) => superbills.filter(s => s.status === status)
);

export default superbillsSlice.reducer;
